/**
 * Complete a test drive from a lead/appointment context.
 *
 * The plain "Complete" on an appointment only closes the appointment with an
 * outcome code. A TEST DRIVE also has an IN_PROGRESS test_drive record that
 * must be closed with the odometer-in reading — that's what the Test Drives
 * module captures, and what the lead-detail panel used to skip. This modal
 * does both in one action: record kmIn + route notes on the drive, then
 * complete the appointment.
 *
 * The drive is resolved from the appointment: we list the lead's test drives
 * and pick the IN_PROGRESS one linked to this appointment. When none is found
 * (e.g. the appointment was checked in before the drive machinery existed) we
 * fall back to just completing the appointment, so the button never dead-ends.
 */

import { useEffect, useState } from 'react';
import { testDrivesService, appointmentsService } from '../lib/services';
import { apiErrorMessage } from '../lib/apiError';

const OUTCOME_OPTIONS = ['SOLD', 'QUOTED', 'FOLLOW_UP', 'NO_SALE'];

export default function CompleteTestDriveModal({ appointment, leadId, onClose, onCompleted }) {
  const resolvedLeadId = leadId ?? appointment.leadId;
  const [drive, setDrive]       = useState(undefined); // undefined=loading, null=none
  const [kmIn, setKmIn]         = useState('');
  const [routeNotes, setNotes]  = useState('');
  const [outcome, setOutcome]   = useState('FOLLOW_UP');
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState(null);

  // Resolve the IN_PROGRESS drive for this appointment from the lead's drives.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await testDrivesService.list({
          leadId: resolvedLeadId,
          status: 'IN_PROGRESS',
          size: 50,
        });
        const list = Array.isArray(rows) ? rows : (rows?.content ?? []);
        const match = list.find(d => d.appointmentId === appointment.id) || list[0] || null;
        if (!cancelled) setDrive(match);
      } catch {
        if (!cancelled) setDrive(null);   // fall back to appointment-only complete
      }
    })();
    return () => { cancelled = true; };
  }, [resolvedLeadId, appointment.id]);

  const MAX_DISTANCE_KM = 20;   // matches the backend sanity cap (DEF-98)
  const kmOut = drive?.kmOut;
  const kmInNum = Number(kmIn);
  const distance = kmOut != null && kmIn !== '' ? kmInNum - kmOut : null;
  const kmInvalid = drive && kmIn !== '' && kmOut != null && kmInNum < kmOut;
  const kmTooFar = distance != null && distance > MAX_DISTANCE_KM;
  // KM in is required only when there's a drive to close.
  const canSubmit = !busy && (!drive || (kmIn !== '' && !kmInvalid && !kmTooFar));

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      if (drive) {
        await testDrivesService.complete(drive.id, {
          kmIn: Number(kmIn) || 0,
          routeNotes: routeNotes.trim() || null,
        });
      }
      // Close the appointment too (independent lifecycle — see backend
      // TestDriveService.complete, which doesn't touch the appointment).
      await appointmentsService.updateStatus(appointment.id, {
        status: 'COMPLETED',
        outcomeCode: outcome,
      });
      onCompleted?.();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not complete the test drive'));
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <form className="modal" style={{ width: 'min(460px, 92vw)' }}
            onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-header">
          <div className="modal-title">Complete test drive</div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {drive === undefined && (
            <div className="mono text-xs text-muted">Finding the open drive…</div>
          )}

          {drive === null && (
            <div className="text-xs text-muted" style={{ lineHeight: 1.6 }}>
              No open test-drive record found for this appointment — it will just
              be marked completed. (If the car did go out, start it from the
              appointment first so the odometer is captured.)
            </div>
          )}

          {drive && (
            <>
              <div className="form-field">
                <label>KM in {kmOut != null && <span className="text-muted">· out at {Number(kmOut).toLocaleString('en-AU')}</span>}</label>
                <input
                  type="number" min={kmOut ?? 0} inputMode="numeric"
                  value={kmIn} onChange={(e) => setKmIn(e.target.value)}
                  placeholder={kmOut != null ? String(kmOut + 10) : 'Odometer reading'}
                  autoFocus
                />
                {kmInvalid && (
                  <div className="text-xs" style={{ color: 'var(--danger)', marginTop: 4 }}>
                    KM in can’t be less than KM out ({Number(kmOut).toLocaleString('en-AU')}).
                  </div>
                )}
                {kmTooFar && (
                  <div className="text-xs" style={{ color: 'var(--danger)', marginTop: 4 }}>
                    That’s a {distance.toLocaleString('en-AU')} km drive — check the reading
                    (a test drive is normally under {MAX_DISTANCE_KM} km).
                  </div>
                )}
              </div>
              <div className="form-field">
                <label>Route notes (optional)</label>
                <textarea rows={2} value={routeNotes} onChange={(e) => setNotes(e.target.value)}
                  placeholder="Highway + city loop, any issues noted" />
              </div>
            </>
          )}

          <div className="form-field">
            <label>Outcome</label>
            <select value={outcome} onChange={(e) => setOutcome(e.target.value)}>
              {OUTCOME_OPTIONS.map(o => <option key={o} value={o}>{o.replace('_', ' ')}</option>)}
            </select>
          </div>

          {error && (
            <div className="text-xs" style={{ color: 'var(--danger)' }}>{error}</div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn small" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn small primary" disabled={!canSubmit}>
            {busy ? 'Completing…' : 'Complete drive'}
          </button>
        </div>
      </form>
    </div>
  );
}
